const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';
const API_BASE = isDev ? '' : 'https://megan-static-host.trackerwanga254.workers.dev';

class ApiClient {
  private token: string | null = null;

  setToken(token: string | null) {
    this.token = token;
    if (token) localStorage.setItem('apexdev_token', token);
    else localStorage.removeItem('apexdev_token');
  }

  getToken(): string | null {
    if (!this.token) this.token = localStorage.getItem('apexdev_token');
    return this.token;
  }

  private async request(path: string, options: RequestInit = {}): Promise<any> {
    const token = this.getToken();
    const headers: Record<string, string> = { ...(options.headers as Record<string, string> || {}) };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (!(options.body instanceof FormData)) headers['Content-Type'] = 'application/json';
    const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Request failed');
    return data;
  }

  async login(email: string, password: string) {
    const data = await this.request('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
    this.setToken(data.token);
    return data;
  }

  async signup(email: string, password: string, username: string) {
    const data = await this.request('/auth/signup', { method: 'POST', body: JSON.stringify({ email, password, username }) });
    this.setToken(data.token);
    return data;
  }

  async getMe() { return this.request('/auth/me'); }
  logout() { this.setToken(null); }

  async getProjects() { return this.request('/api/projects'); }
  async createProject(name: string) { return this.request('/api/projects', { method: 'POST', body: JSON.stringify({ name }) }); }
  async getProject(id: string) { return this.request(`/api/projects/${id}`); }
  async updateProject(id: string, u: any) { return this.request(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify(u) }); }
  async deleteProject(id: string) { return this.request(`/api/projects/${id}`, { method: 'DELETE' }); }

  async getFiles(pid: string) { return this.request(`/api/projects/${pid}/files`); }
  async getFileContent(pid: string, fn: string): Promise<string> {
    const res = await fetch(`${API_BASE}/api/projects/${pid}/files/${encodeURIComponent(fn)}`, {
      headers: { 'Authorization': `Bearer ${this.getToken()}` }
    });
    if (!res.ok) throw new Error('File not found');
    return res.text();
  }
  async updateFile(pid: string, fn: string, content: string) {
    return this.request(`/api/projects/${pid}/files/${encodeURIComponent(fn)}`, { method: 'PATCH', body: JSON.stringify({ content }) });
  }
  async deleteFile(pid: string, fn: string) {
    return this.request(`/api/projects/${pid}/files/${encodeURIComponent(fn)}`, { method: 'DELETE' });
  }
  async uploadFiles(pid: string, files: File[]) {
    const fd = new FormData();
    files.forEach(f => fd.append(f.webkitRelativePath || f.name, f));
    const res = await fetch(`${API_BASE}/api/projects/${pid}/files`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.getToken()}` },
      body: fd
    });
    return res.json();
  }
  async uploadZip(pid: string, file: File) {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/api/projects/${pid}/zip`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.getToken()}` },
      body: fd
    });
    return res.json();
  }
  async renameFile(pid: string, oldName: string, newName: string) {
    return this.request(`/api/projects/${pid}/rename`, { method: 'POST', body: JSON.stringify({ oldName, newName }) });
  }

  async deploy(pid: string) { return this.request(`/api/projects/${pid}/deploy`, { method: 'POST' }); }
  async getDeployments(pid: string) { return this.request(`/api/projects/${pid}/deployments`); }
  async getVersions(pid: string) { return this.request(`/api/projects/${pid}/versions`); }

  async getEnvVars(pid: string) { return this.request(`/api/projects/${pid}/env`); }
  async setEnvVars(pid: string, env: Record<string, string>) {
    return this.request(`/api/projects/${pid}/env`, { method: 'PUT', body: JSON.stringify({ env }) });
  }
  async deleteEnvVar(pid: string, key: string) { return this.request(`/api/projects/${pid}/env/${key}`, { method: 'DELETE' }); }
  async get404Status(pid: string) { return this.request(`/api/projects/${pid}/settings/404`); }
  async set404Page(pid: string, content: string) {
    return this.request(`/api/projects/${pid}/settings/404`, { method: 'PUT', body: JSON.stringify({ content }) });
  }
  async remove404Page(pid: string) {
    return this.request(`/api/projects/${pid}/settings/404`, { method: 'PUT', body: JSON.stringify({ delete: true }) });
  }

  async getAnalytics(pid: string) { return this.request(`/api/projects/${pid}/analytics`); }
  async getAccount() { return this.request('/api/account'); }

  async aiChat(msg: string, ctx?: string) {
    return this.request('/api/ai/chat', { method: 'POST', body: JSON.stringify({ message: msg, context: ctx }) });
  }
  async scanFiles(files: { name: string; content: string }[]) {
    return this.request('/api/ai/scan', { method: 'POST', body: JSON.stringify({ files }) });
  }
}

export const api = new ApiClient();
